import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import { NotificationType } from "@/types/eventNotifications";
import {
  CreateNotificationTypeRequest,
  CreateNotificationTypeResponse,
} from "@/api/createNotificationType/types";
import {
  EditNotificationTypeRequest,
  EditNotificationTypeResponse,
} from "@/api/editNotificationType/types";
import {
  DeleteNotificationTypeRequest,
  DeleteNotificationTypeResponse,
} from "@/api/deleteNotificationType/types";
import {
  FetchNotificationTypesRequest,
  FetchNotificationTypesResponse,
} from "@/api/fetchNotificationTypes/types";
import {
  CheckPendingNotificationsRequest,
  CheckPendingNotificationsResponse,
} from "@/api/checkPendingNotifications/types";
import { createNotificationType } from "@/api/createNotificationType/createNotificationTypeApi";
import { editNotificationType } from "@/api/editNotificationType/editNotificationTypeApi";
import { deleteNotificationType } from "@/api/deleteNotificationType/deleteNotificationTypeApi";
import { fetchNotificationTypes } from "@/api/fetchNotificationTypes/fetchNotificationTypesApi";
import { checkPendingNotifications } from "@/api/checkPendingNotifications/checkPendingNotificationsApi";

interface NotificationTypeSliceState {
  loadingCreate: boolean;
  createError: string | null;
  createdNotificationType: NotificationType | null;

  loadingGet: boolean;
  getError: string | null;
  fetchedNotificationType: NotificationType | null;

  loadingUpdate: boolean;
  updateError: string | null;
  updatedNotificationType: NotificationType | null;

  loadingDelete: boolean;
  deleteError: string | null;
  deletedNotificationTypeId: string | null;

  loadingList: boolean;
  listError: string | null;
  notificationTypesList: NotificationType[];
  totalCount: number;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
  } | null;

  loadingPendingCount: boolean;
  pendingCountError: string | null;
  pendingCount: number;
}

const initialState: NotificationTypeSliceState = {
  loadingCreate: false,
  createError: null,
  createdNotificationType: null,

  loadingGet: false,
  getError: null,
  fetchedNotificationType: null,

  loadingUpdate: false,
  updateError: null,
  updatedNotificationType: null,

  loadingDelete: false,
  deleteError: null,
  deletedNotificationTypeId: null,

  loadingList: false,
  listError: null,
  notificationTypesList: [],
  totalCount: 0,
  pagination: null,

  loadingPendingCount: false,
  pendingCountError: null,
  pendingCount: 0,
};

const notificationTypeSlice = createSlice({
  name: "notificationType",
  initialState,
  reducers: {
    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },
    setCreatedNotificationType(
      state,
      action: PayloadAction<NotificationType | null>,
    ) {
      state.createdNotificationType = action.payload;
    },

    setLoadingGet(state, action: PayloadAction<boolean>) {
      state.loadingGet = action.payload;
    },
    setGetError(state, action: PayloadAction<string | null>) {
      state.getError = action.payload;
    },
    setFetchedNotificationType(
      state,
      action: PayloadAction<NotificationType | null>,
    ) {
      state.fetchedNotificationType = action.payload;
    },

    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdateError(state, action: PayloadAction<string | null>) {
      state.updateError = action.payload;
    },
    setUpdatedNotificationType(
      state,
      action: PayloadAction<NotificationType | null>,
    ) {
      state.updatedNotificationType = action.payload;
    },

    setLoadingDelete(state, action: PayloadAction<boolean>) {
      state.loadingDelete = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
    setDeletedNotificationTypeId(state, action: PayloadAction<string | null>) {
      state.deletedNotificationTypeId = action.payload;
    },

    setLoadingList(state, action: PayloadAction<boolean>) {
      state.loadingList = action.payload;
    },
    setListError(state, action: PayloadAction<string | null>) {
      state.listError = action.payload;
    },
    setNotificationTypesList(state, action: PayloadAction<NotificationType[]>) {
      state.notificationTypesList = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setPagination(
      state,
      action: PayloadAction<{
        page: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) {
      state.pagination = action.payload;
    },

    setLoadingPendingCount(state, action: PayloadAction<boolean>) {
      state.loadingPendingCount = action.payload;
    },
    setPendingCountError(state, action: PayloadAction<string | null>) {
      state.pendingCountError = action.payload;
    },
    setPendingCount(state, action: PayloadAction<number>) {
      state.pendingCount = action.payload;
    },

    resetNotificationTypeState(state) {
      state.loadingCreate = false;
      state.createError = null;
      state.createdNotificationType = null;

      state.loadingGet = false;
      state.getError = null;
      state.fetchedNotificationType = null;

      state.loadingUpdate = false;
      state.updateError = null;
      state.updatedNotificationType = null;

      state.loadingDelete = false;
      state.deleteError = null;
      state.deletedNotificationTypeId = null;

      state.loadingList = false;
      state.listError = null;
      state.notificationTypesList = [];
      state.totalCount = 0;
      state.pagination = null;

      state.loadingPendingCount = false;
      state.pendingCountError = null;
      state.pendingCount = 0;
    },
  },
});

export const {
  setLoadingCreate,
  setCreateError,
  setCreatedNotificationType,

  setLoadingGet,
  setGetError,
  setFetchedNotificationType,

  setLoadingUpdate,
  setUpdateError,
  setUpdatedNotificationType,

  setLoadingDelete,
  setDeleteError,
  setDeletedNotificationTypeId,

  setLoadingList,
  setListError,
  setNotificationTypesList,
  setTotalCount,
  setPagination,

  setLoadingPendingCount,
  setPendingCountError,
  setPendingCount,

  resetNotificationTypeState,
} = notificationTypeSlice.actions;

export default notificationTypeSlice.reducer;

export const createNotificationTypeAction =
  (
    requestData: CreateNotificationTypeRequest,
    onSuccess?: (createdData: NotificationType) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    dispatch(setCreateError(null));

    try {
      const response = await createNotificationType(requestData);
      dispatch(setCreatedNotificationType(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create notification type.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const editNotificationTypeAction =
  (
    requestData: EditNotificationTypeRequest,
    onSuccess?: (updatedData: NotificationType) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    dispatch(setUpdateError(null));

    try {
      const response = await editNotificationType(requestData);
      dispatch(setUpdatedNotificationType(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update notification type.";
      dispatch(setUpdateError(message));
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const deleteNotificationTypeAction =
  (requestData: DeleteNotificationTypeRequest, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingDelete(true));
    dispatch(setDeleteError(null));

    try {
      await deleteNotificationType(requestData);
      dispatch(setDeletedNotificationTypeId(requestData.id));
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to delete notification type.";
      dispatch(setDeleteError(message));
    } finally {
      dispatch(setLoadingDelete(false));
    }
  };

export const fetchNotificationTypesAction =
  (
    requestData: FetchNotificationTypesRequest = {},
    onSuccess?: (notificationTypes: NotificationType[]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingList(true));
    dispatch(setListError(null));

    try {
      const response = await fetchNotificationTypes(requestData);
      const { data: notificationTypesArray, pagination } = response;
      dispatch(setNotificationTypesList(notificationTypesArray));
      dispatch(setTotalCount(pagination.totalCount));
      dispatch(
        setPagination({
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalPages: pagination.totalPages,
        }),
      );

      if (onSuccess) {
        onSuccess(notificationTypesArray);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch notification types.";
      dispatch(setListError(message));
    } finally {
      dispatch(setLoadingList(false));
    }
  };

export const checkPendingNotificationsAction =
  (
    requestData: CheckPendingNotificationsRequest,
    onSuccess?: (count: number) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingPendingCount(true));
    dispatch(setPendingCountError(null));

    try {
      const response = await checkPendingNotifications(requestData);
      dispatch(setPendingCount(response.count));
      if (onSuccess) {
        onSuccess(response.count);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to check pending notifications.";
      dispatch(setPendingCountError(message));
    } finally {
      dispatch(setLoadingPendingCount(false));
    }
  };
